import React, { Component } from 'react';

import Button from '@atlaskit/button/standard-button';
import DropdownMenu, {
  DropdownItem,
  DropdownItemGroup,
} from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/glyph/more';
import Tooltip from '@atlaskit/tooltip';

import { FEATURE_FLAG, IS_CLOUD } from '@qality/common-config';
import { messages } from '@qality/common-messages';
import { TestCase } from '@qality/common-model';
import {
  StorageOfUniqueDialogs,
  StorageOfUniqueServices,
} from '@qality/common-service';
import FocusUtils from '@qality/common-utils/src/FocusUtils';

import { FocusInterceptor } from '../atoms/FocusInterceptor';
import './_test-case-box-actions.scss';

type Props = {
  canEdit: boolean;
  addTestStep: () => Promise<boolean | void>;
  onDeleteTestCase: () => void;
  testCase: TestCase;
  issueKey: string;
  isTestStepView?: boolean;
  onDropdownChange?: () => void;
  fullScreenProps: {
    isFullscreenMode: boolean;
    onOpen: () => void;
    onClose: () => void;
  };
};

export default class TestCaseBoxActions extends Component<Props> {
  constructor(props: Props) {
    super(props);
  }

  getFullscreenActions = (): { label: string; onClick: () => void } => {
    if (this.props.fullScreenProps.isFullscreenMode) {
      return {
        label: 'Close full-screen mode',
        onClick: this.props.fullScreenProps.onClose,
      };
    }

    return {
      label: 'Open Test Case in full-screen mode',
      onClick: this.props.fullScreenProps.onOpen,
    };
  };

  testCaseExecutionDialog = async (): Promise<void> => {
    const testCaseId = (await this.props.testCase.issue).id;
    StorageOfUniqueDialogs.get(
      'TestCaseExecutionDialog'
    ).openTestCaseExecutionDialog(testCaseId, this.props.testCase.name);
  };

  openTestCaseExecutionHistoryPage = async (): Promise<void> => {
    const testCaseKey = (await this.props.testCase.issue).key;
    const url = await StorageOfUniqueServices.get(
      'URLService'
    ).getTestCaseExecutionHistoryUrl(testCaseKey, this.props.issueKey);

    window.open(url, '_parent');
  };

  openAddToTestCycleDialog = async (): Promise<void> => {
    const testCaseId = (await this.props.testCase.issue).id;
    await StorageOfUniqueDialogs.get(
      'AddToTestCycleDialogService'
    ).openAddToTestCycleDialog([testCaseId]);
  };

  getExecutionGroup(): JSX.Element {
    const actions = [];

    if (this.props.canEdit) {
      actions.push(
        <DropdownItem
          key='execute'
          onClick={() => this.testCaseExecutionDialog()}
        >
          <div className='text-case-actions-wrapper'>
            <div className='text-case-actions-label'>
              {messages['testCaseBoxActions.execute.label']}
            </div>
          </div>
        </DropdownItem>
      );
    }

    actions.push(
      <DropdownItem
        key='execute-history'
        onClick={() => this.openTestCaseExecutionHistoryPage()}
      >
        <div className='text-case-actions-wrapper'>
          <div className='text-case-actions-label'>
            {messages['testCaseBoxActions.execute.history.label']}
          </div>
        </div>
      </DropdownItem>
    );

    return <DropdownItemGroup title='EXECUTION'>{actions}</DropdownItemGroup>;
  }

  getManageTestCaseGroup(): JSX.Element {
    const actions = [];

    if (this.props.canEdit) {
      actions.push(
        <DropdownItem key='add' onClick={() => this.props.addTestStep()}>
          <div className='text-case-actions-wrapper'>
            <div className='text-case-actions-label'>
              {messages['testCaseBoxActions.add.label']}
            </div>
          </div>
        </DropdownItem>
      );

      actions.push(
        <DropdownItem
          key='add-to-test-cycle'
          onClick={this.openAddToTestCycleDialog}
        >
          <div className='text-case-actions-wrapper'>
            <div className='text-case-actions-label'>
              {messages['testCaseBoxActions.add.to.tet.cycle.label']}
            </div>
          </div>
        </DropdownItem>
      );

      if (!this.props.isTestStepView) {
        actions.push(
          <DropdownItem
            key='delete'
            onClick={() => this.props.onDeleteTestCase()}
          >
            <div className='text-case-actions-wrapper'>
              <div className='text-case-actions-label'>
                {messages['testCaseBoxActions.delete.label']}
              </div>
            </div>
          </DropdownItem>
        );
      }
    }

    return actions.length ? (
      <DropdownItemGroup title='MANAGE TEST CASE'>{actions}</DropdownItemGroup>
    ) : null;
  }

  getOtherGroup(): JSX.Element {
    const actions = [];
    const { label, onClick } = this.getFullscreenActions();
    const tooltipContent = FEATURE_FLAG.isPremium
      ? ''
      : 'Editing test case in full-screen available only in QAlity Plus.';

    if (this.props.canEdit) {
      actions.push(
        <Tooltip content={tooltipContent}>
          <DropdownItem
            key='fullscreen'
            onClick={onClick}
            isDisabled={!FEATURE_FLAG.isPremium}
          >
            <div className='text-case-actions-wrapper'>
              <div className='text-case-actions-label'>{label}</div>
            </div>
          </DropdownItem>
        </Tooltip>
      );
    }

    return <DropdownItemGroup title='OTHER'>{actions}</DropdownItemGroup>;
  }

  getHelpGroup(): JSX.Element {
    const actions = [];

    actions.push(
      <DropdownItem
        key='delete'
        onClick={() =>
          window.open(
            'https://soldevelo.atlassian.net/wiki/spaces/QS/pages/4261969968/Keyboard+Shortcuts',
            '_blank'
          )
        }
      >
        <div className='text-case-actions-wrapper'>
          <div className='text-case-actions-label'>
            Show keyboard shortcuts list
          </div>
        </div>
      </DropdownItem>
    );

    actions.push(
      <DropdownItem
        key='delete'
        onClick={() =>
          window.open(
            'https://soldevelo.atlassian.net/wiki/spaces/QS/overview?homepageId=1631322237',
            '_blank'
          )
        }
      >
        <div className='text-case-actions-wrapper'>
          <div className='text-case-actions-label'>Browse documentation</div>
        </div>
      </DropdownItem>
    );

    actions.push(
      <DropdownItem
        key='delete'
        onClick={() => window.open(plugin.feedbackUrl, '_blank')}
      >
        <div className='text-case-actions-wrapper'>
          <div className='text-case-actions-label'>Contact Support</div>
        </div>
      </DropdownItem>
    );

    return <DropdownItemGroup title='NEED HELP?'>{actions}</DropdownItemGroup>;
  }

  getActions(): Array<JSX.Element> {
    return [
      this.getExecutionGroup(),
      this.getManageTestCaseGroup(),
      this.getOtherGroup(),
      this.getHelpGroup(),
    ];
  }

  render(): JSX.Element {
    const itemGroups = this.getActions();
    return (
      <>
        {itemGroups.length ? (
          <div className='text-case-box-actions'>
            <FocusInterceptor
              render={(interceptFocus) => {
                return (
                  <DropdownMenu
                    shouldFlip={false}
                    placement='left-start'
                    onOpenChange={(data) => {
                      // @ts-expect-error for compatibility reasons the data is still send to both functions
                      this.props.onDropdownChange(data);
                      if (IS_CLOUD) {
                        FocusUtils.unfocusActiveElement();
                      } else {
                        // @ts-expect-error for compatibility reasons the data is still send to both functions
                        interceptFocus(data);
                      }
                    }}
                    trigger={({ triggerRef, ...props }) => (
                      <Button
                        {...props}
                        iconBefore={
                          <MoreIcon
                            size='medium'
                            primaryColor='var(--ds-icon, var(--ds-fb-icon))'
                            label='moreIcon'
                          />
                        }
                        ref={triggerRef}
                        appearance='subtle'
                      />
                    )}
                  >
                    {itemGroups}
                  </DropdownMenu>
                );
              }}
            />
          </div>
        ) : null}
      </>
    );
  }
}
